import { createReducer, on, Action } from '@ngrx/store';

import * as ChatActions from './chat.actions';
import {
  ConnectionState,
  IChatAuth,
  IChatMessage
} from '@care-portals/api-interfaces';

export const CHAT_FEATURE_KEY = 'chat';

export interface State {
  chatId: string;
  chatCount: number;
  isInitialized: boolean;
  isReady: boolean;
  connectionState: ConnectionState;
  statusString: string;
  loading: boolean; //  if the appiontment list is loading
  error?: string | null; // last none error (if any)
  auth: IChatAuth;
  identity: string;
  messages: IChatMessage[];
  newMessages: IChatMessage[];
  loadMoreMessages: IChatMessage[];
  participants: any[];
  videoRoomId: string;
}

// export const chatAdapter: EntityAdapter<ChatEntity> = createEntityAdapter<
//   ChatEntity
// >({
//   selectId: selectChatId
// });

export const initialState: State = {
  // set initial required properties
  isInitialized: false,
  chatCount: 0,
  isReady: false,
  connectionState: 'disconnected',
  statusString: 'Not Connected',
  loading: false,
  chatId: null,
  auth: null,
  identity: null,
  messages: [],
  newMessages: [],
  loadMoreMessages: [],
  participants: [],
  videoRoomId: null
};

const chatReducer = createReducer(
  initialState,
  on(ChatActions.updateChatCount, (state, { chatId, count }) => {
    return { ...state, chatCount: count };
  }),
  on(ChatActions.startEncounterSuccess, (state, { response }) => ({
    ...state,
    chatId: response.chatId,
    auth: response.auth,
    identity: response.identity,
    loading: false,
    connectionState: 'disconnected' as ConnectionState,
    statusString: 'Started',
    messages: [],
    participants: []
  })),

  on(ChatActions.participantJoined, (state, { participants }) => {
    return {
      ...state,
      participants
    };
  }),
  on(ChatActions.participantLeft, (state, { participants }) => {
    return {
      ...state,
      participants
    };
  }),
  on(ChatActions.refreshTokenSuccess, (state, { auth }) => {
    return {
      ...state,
      auth
    };
  }),
  on(ChatActions.connectionStateChanged, (state, { connectionState }) => {
    if (connectionState === 'connecting') {
      return {
        ...state,
        connectionState,
        statusString: 'Connecting...'
      };
    } else if (connectionState === 'connected') {
      return {
        ...state,
        connectionState,
        statusString: 'You are connected'
      };
    } else if (connectionState === 'disconnecting') {
      return {
        ...state,
        connectionState,
        statusString: 'Disconnecting...',
        isReady: false
      };
    } else if (connectionState === 'disconnected') {
      return {
        ...state,
        connectionState,
        statusString: 'Disconnected',
        isReady: false
      };
    } else if (connectionState === 'denied') {
      return {
        ...state,
        connectionState,
        statusString: 'Failed to connect',
        isReady: false
      };
    } else {
      return {
        ...state,
        connectionState
      };
    }
  }),
  on(ChatActions.loadMessages, state => {
    return { ...state, loading: false };
  }),
  on(ChatActions.loadMessagesSuccess, (state, { messages }) => {
    return {
      ...state,
      messages: [...state.messages, ...messages],
      newMessages: [...messages],
      loading: false
    };
  }),
  on(ChatActions.loadMessagesFailure, (state, { error }) => {
    return {
      ...state,
      loading: false
    };
  }),
  on(ChatActions.loadMore, state => {
    return { ...state, loading: true };
  }),
  on(ChatActions.loadMoreSuccess, (state, { messages }) => {
    return {
      ...state,
      messages: [...messages, ...state.messages],
      loadMoreMessages: [...messages],
      loading: false
    };
  }),
  on(ChatActions.messageAdded, (state, { chatId, message }) => {
    return {
      ...state,
      chatId,
      messages: [...state.messages, ...state.newMessages],
      newMessages: [message]
    };
  }),
  on(ChatActions.closeChat, state => ({
    ...state,
    chatId: null,
    videoRoomId: null,
    messages: [],
    loadMoreMessages: [],
    newMessages: [],
    loading: false,
    chatCount: 0,
    participants: [],
    auth: null,
    identity: null,
    connectionState: 'disconnected' as ConnectionState,
    isInitialized: false,
    isReady: false,
    statusString: 'Closed'
  })),

  // VIDEO STATE MUTATIONS
  on(ChatActions.joinVideoSuccess, (state, { videoRoomId }) => ({
    ...state,
    videoRoomId
  })),
  on(ChatActions.joinVideoFailure, (state, { error }) => ({
    ...state,
    videoRoomId: null
  })),
  on(ChatActions.leaveRoomSuccess, state => ({
    ...state,
    videoRoomId: null
  })),
  on(ChatActions.leaveRoomFailure, (state, { error }) => ({
    ...state
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return chatReducer(state, action);
}
